/* eslint-disable import-extensions/file-extension-required */
import set from 'lodash/set';

import { getLogger } from '@confluence/logger';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import { REPLY_TO_PAGE_COMMENT_PUBLISH_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import type { ExperienceTrackerAPI } from '@confluence/experience-tracker/entry-points/ExperienceTracker.ts';
import { END } from '@confluence/navdex/entry-points/NavdexPointType.ts';
import { CommentType } from '@confluence/comments-util/entry-points/analytics.ts';
import { getCommentsDataManagerExperimentAttributes } from '@confluence/experience-tracker/entry-points/getCommentsDataManagerExperimentAttributes';

import { DEFAULT_LIMIT } from './index.ts';

import {
	CommentsSectionQuery,
	CommentsSectionWithoutReactionsQuery,
} from './CommentsSectionQuery.graphql.ts';
import type { CommentsSectionQuery as CommentsSectionQueryType } from './__types__/CommentsSectionQuery.ts';
import type { CommentsSectionWithoutReactionsQuery as CommentsSectionWithoutReactionsQueryType } from './__types__/CommentsSectionQueryWithoutReactionsQuery.ts';
import { readQueryInterop } from '@confluence/apollo-relay-interop/entry-points/readQuery.ts';
import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';

const logger = getLogger('page-comments-queries');

export const updateFooterApolloCacheOnEdit = (
	commentId: string,
	newBody: string,
	pageId: string,
	isReactionsEnabled: boolean,
	sessionDisplayName: string | null,
): void => {
	const commentsSectionQuery = isReactionsEnabled
		? CommentsSectionQuery
		: CommentsSectionWithoutReactionsQuery;

	try {
		// Update the comments section data
		const commentsSectionCacheData = readQueryInterop<
			CommentsSectionQueryType | CommentsSectionWithoutReactionsQueryType
		>({
			query: commentsSectionQuery,
			variables: {
				contentId: pageId,
				offset: '',
				first: DEFAULT_LIMIT,
			},
		});

		if (commentsSectionCacheData) {
			// grab the parent comment for the comments panel
			const commentEdges = [...(commentsSectionCacheData?.comments?.edges || [])];
			const commentIdx = commentEdges?.findIndex(
				(commentEdge) => commentEdge?.node?.id === commentId,
			);

			if (
				commentIdx !== undefined &&
				commentIdx !== null &&
				commentIdx !== -1 &&
				commentEdges[commentIdx]?.node?.body &&
				commentEdges[commentIdx]?.node?.version?.number
			) {
				// Find the index to update. TS will throw an error even though we've already checked the index is valid
				// @ts-ignore
				commentEdges[commentIdx].node.body.value = newBody; // Update the body we keep in the footer cache
				// @ts-ignore
				commentEdges[commentIdx].node.version.number += 1; // This is required for the propagated footer version to also be editable
				// @ts-ignore
				set(commentEdges[commentIdx].node, 'version.by.displayName', sessionDisplayName); // Update the display name to the user who edited the comment
				// @ts-ignore
				set(commentEdges[commentIdx].node, 'version.when', new Date().toISOString()); // Update the version date to the date of the edit
			}

			writeQueryInterop({
				query: commentsSectionQuery,
				variables: {
					contentId: pageId,
					offset: '',
					first: DEFAULT_LIMIT,
				},
				data: {
					...commentsSectionCacheData,
					comments: {
						...commentsSectionCacheData.comments,
						totalCount: commentEdges.length,
						pageInfo: {
							...commentsSectionCacheData.comments?.pageInfo,
							endCursor: Math.random().toString(), // The cache won't update without this. Should be harmless, given hasNextPage is false, so we won't be loading more
							hasNextPage: false,
						},
						edges: commentEdges,
					},
				},
			});
		}
	} catch (err) {
		logger.error`An Error occurred when updating cache for edit footer reply - ${err}`;
	}
};

interface HandleCreateGeneralReplySuccessProps {
	onSuccess: any;
	editCommentQueryId?: string;
	setCommentForEdit?: any;
	data: any;
	createAnalyticsEvent: any;
	pageId: string | undefined;
	pageType: string;
	analyticsSource: string;
	commentId: string | undefined;
	pageMode: PageMode;
	experienceTracker: ExperienceTrackerAPI;
	searchSessionId?: string | null;
	isFooterComment?: boolean;
}

export const handleCreateReplySuccess = ({
	onSuccess,
	editCommentQueryId,
	setCommentForEdit,
	data,
	createAnalyticsEvent,
	pageId,
	pageType,
	analyticsSource,
	commentId,
	pageMode,
	experienceTracker,
	searchSessionId,
	isFooterComment,
}: HandleCreateGeneralReplySuccessProps): void => {
	onSuccess();
	if (editCommentQueryId) {
		setCommentForEdit && setCommentForEdit('');
	}

	const replyCommentInfo = data?.createFooterComment;

	// Find the reply button container and scroll to it if needed
	if (replyCommentInfo?.id && commentId && isFooterComment) {
		// First find the container
		// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
		const container = document.getElementById(`footer-comment-thread-${commentId}-container`);

		if (container) {
			// Set up observer to watch for the reply button in the container
			const observer = new MutationObserver((_mutations, obs) => {
				const replyButtonContainer = container.querySelector(
					'[data-testid="comments-panel-reply-comment-action"]',
				);

				if (replyButtonContainer) {
					obs.disconnect();

					// Check if the element is not in viewport, accounting for 75px add comment prompt at bottom
					const rect = replyButtonContainer.getBoundingClientRect();
					// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
					const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
					const isInViewport =
						rect.top >= 0 &&
						rect.left >= 0 &&
						rect.bottom <= viewportHeight - 75 && // Account for add comment prompt
						// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
						rect.right <= (window.innerWidth || document.documentElement.clientWidth);

					if (!isInViewport) {
						replyButtonContainer.scrollIntoView({
							behavior: 'smooth',
							block: 'center',
						});
					}
				}
			});

			observer.observe(container, {
				childList: true,
				subtree: true,
			});

			// Cleanup observer after 2 seconds to prevent memory leaks
			setTimeout(() => {
				observer.disconnect();
			}, 2000);
		}
	}

	createAnalyticsEvent({
		type: 'sendTrackEvent',
		data: {
			action: 'created',
			actionSubject: 'comment',
			actionSubjectId: replyCommentInfo?.id, // The newly created comment ID
			objectType: pageType,
			objectId: pageId,
			source: analyticsSource,
			attributes: {
				...getCommentsDataManagerExperimentAttributes(),
				commentType: CommentType.GENERAL,
				source: analyticsSource,
				pageType,
				isReply: true,
				pageMode,
				isLivePage: pageMode === PageMode.LIVE,
				editor: 'v2',
				searchSessionId,
				navdexPointType: END,
				parentCommentId: commentId ?? null, // analytics event schema type expects string or null
			},
		},
	}).fire();

	experienceTracker.succeed({
		name: REPLY_TO_PAGE_COMMENT_PUBLISH_EXPERIENCE,
	});
};
